/**
 * LoadingState Component - Displays loading states consistently
 * Provides skeleton loaders and spinners for better perceived performance
 */

import { Loader2, Zap, MapPin, Car, Shield, Heart, Landmark, Wallet } from 'lucide-react';
import { commonStyles } from '../../../styles/common';

type SkeletonVariant = 'address' | 'personal' | 'vehicle' | 'insurance' | 'health' | 'financial';

// Cache keys for storing item counts per category
const SKELETON_COUNT_CACHE_KEY = 'lucidDock_skeletonCounts';
const SKELETON_FAVORITES_CACHE_KEY = 'lucidDock_skeletonFavorites';

/**
 * Get cached item count for a category to show matching skeleton cards
 * Falls back to 1 if no cache exists
 */
export function getCachedItemCount(category: SkeletonVariant): number {
  try {
    const cached = localStorage.getItem(SKELETON_COUNT_CACHE_KEY);
    if (cached) {
      const counts = JSON.parse(cached);
      // Use nullish coalescing so that 0 is respected (not treated as falsy)
      return counts[category] ?? 1;
    }
  } catch {
    // Ignore parse errors
  }
  return 1;
}

/**
 * Get cached item count with 0 default (for sidebar badges, not skeletons).
 * Returns 0 when no cache exists so badges only show after data has loaded.
 */
export function getCachedSectionCount(category: SkeletonVariant): number {
  try {
    const cached = localStorage.getItem(SKELETON_COUNT_CACHE_KEY);
    if (cached) {
      const counts = JSON.parse(cached);
      return counts[category] ?? 0;
    }
  } catch { /* ignore */ }
  return 0;
}

/**
 * Get cached favorites count for a category
 * Falls back to 0 if no cache exists
 */
export function getCachedFavoritesCount(category: SkeletonVariant): number {
  try {
    const cached = localStorage.getItem(SKELETON_FAVORITES_CACHE_KEY);
    if (cached) {
      const counts = JSON.parse(cached);
      return counts[category] || 0;
    }
  } catch {
    // Ignore parse errors
  }
  return 0;
}

/**
 * Cache item counts for skeleton loaders
 * Call this when data loads to ensure skeletons match actual data
 */
export function cacheItemCounts(counts: Partial<Record<SkeletonVariant, number>>): void {
  try {
    const existing = localStorage.getItem(SKELETON_COUNT_CACHE_KEY);
    const current = existing ? JSON.parse(existing) : {};
    const updated = { ...current, ...counts };
    localStorage.setItem(SKELETON_COUNT_CACHE_KEY, JSON.stringify(updated));
    // Notify sidebar (and any other listener) that counts changed
    window.dispatchEvent(new CustomEvent('luciddock-counts-updated'));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Cache favorites counts for skeleton loaders
 * Call this when data loads to ensure Quick Access section matches actual data
 */
export function cacheFavoritesCounts(counts: Partial<Record<SkeletonVariant, number>>): void {
  try {
    const existing = localStorage.getItem(SKELETON_FAVORITES_CACHE_KEY);
    const current = existing ? JSON.parse(existing) : {};
    const updated = { ...current, ...counts };
    localStorage.setItem(SKELETON_FAVORITES_CACHE_KEY, JSON.stringify(updated));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Sync skeleton counts from Google Drive profile metadata
 * Call this during app initialization for Pro users to populate cache from Google Drive
 * This provides accurate skeleton counts on first page load (even across devices)
 */
export function syncCountsFromGoogleDriveMeta(summaryCounts: {
  addresses: number;
  vehicles: number;
  insurancePolicies: number;
}): void {
  cacheItemCounts({
    address: summaryCounts.addresses,
    vehicle: summaryCounts.vehicles,
    insurance: summaryCounts.insurancePolicies,
  });
}

interface LoadingStateProps {
  message?: string;
  variant?: 'spinner' | 'skeleton';
  skeletonVariant?: SkeletonVariant;
  className?: string;
}

export function LoadingState({
  message = 'Loading...',
  variant = 'spinner',
  skeletonVariant = 'address',
  className = ''
}: LoadingStateProps) {
  if (variant === 'skeleton') {
    return <SkeletonLoader className={className} variant={skeletonVariant} />;
  }

  return (
    <div className={`${commonStyles.flexCenter} flex-col py-12 ${className}`}>
      <Loader2 className={`${commonStyles.iconSizes['3xl']} ${commonStyles.colors.text.gray.accessible} ${commonStyles.animations.spin}`} />
      <p className={`mt-4 ${commonStyles.font.sm} ${commonStyles.colors.text.gray.accessible}`}>
        {message}
      </p>
    </div>
  );
}

// Base skeleton card wrapper
function SkeletonCard({ children }: { children: React.ReactNode }) {
  return (
    <div className={`${commonStyles.colors.background.white} ${commonStyles.radius['3xl']} p-4 sm:p-6 border ${commonStyles.colors.border.gray.light} ${commonStyles.shadows.md} animate-pulse h-full flex flex-col`}>
      {children}
    </div>
  );
}

// Address Card Skeleton - matches AddressCard layout
export function AddressCardSkeleton() {
  return (
    <SkeletonCard>
      {/* Header: Title + Checkmark (circle) + Star (circle) + Badge (pill) */}
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="h-6 w-32 bg-gray-200 rounded"></div>
          <div className="w-5 h-5 bg-green-100 rounded-full"></div>
          <div className="w-5 h-5 bg-amber-100 rounded-full"></div>
          <div className="h-6 w-14 bg-indigo-100 rounded-full"></div>
        </div>
        <div className="w-8 h-8 bg-gray-200 rounded-xl"></div>
      </div>

      {/* Content: Map pin + Address line */}
      <div className="space-y-3 flex-1">
        <div className="flex items-start gap-1.5">
          <div className="w-4 h-4 bg-gray-200 rounded-full flex-shrink-0 mt-0.5"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 w-full bg-gray-200 rounded"></div>
            <div className="h-4 w-2/3 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>

      {/* Footer: Links pill button + icon badges */}
      <div className="mt-auto pt-4">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-3"></div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Links pill with count badge */}
            <div className="flex items-center gap-1.5 h-6 px-2.5 bg-purple-100 rounded-full">
              <div className="w-3.5 h-3.5 bg-purple-200 rounded-full"></div>
              <div className="w-8 h-3 bg-purple-200 rounded"></div>
              <div className="w-4 h-4 bg-purple-300 rounded-full"></div>
            </div>
            {/* Icon badges: Contacts, Service Providers, Utilities */}
            <div className="flex items-center gap-2">
              <div className="relative w-6 h-6">
                <div className="w-4 h-4 bg-blue-100 rounded"></div>
                <div className="absolute -top-0.5 -right-1.5 w-3.5 h-3.5 bg-blue-200 rounded-full"></div>
              </div>
              <div className="relative w-6 h-6">
                <div className="w-4 h-4 bg-cyan-100 rounded"></div>
                <div className="absolute -top-0.5 -right-1.5 w-3.5 h-3.5 bg-cyan-200 rounded-full"></div>
              </div>
              <div className="relative w-6 h-6">
                <div className="w-4 h-4 bg-emerald-100 rounded"></div>
                <div className="absolute -top-0.5 -right-1.5 w-3.5 h-3.5 bg-emerald-200 rounded-full"></div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    </SkeletonCard>
  );
}

// Vehicle Card Skeleton - matches VehicleCard layout
export function VehicleCardSkeleton() {
  return (
    <SkeletonCard>
      {/* Header: Year Make Model + Star (circle) + Type Badge (pill) */}
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="h-6 w-40 bg-gray-200 rounded"></div>
          <div className="w-5 h-5 bg-amber-100 rounded-full"></div>
          <div className="h-6 w-14 bg-indigo-100 rounded-full"></div>
        </div>
        <div className="w-8 h-8 bg-gray-200 rounded-xl"></div>
      </div>

      {/* Content: License plate, VIN, Purchase date */}
      <div className="space-y-3 flex-1">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 rounded"></div>
          <div className="h-4 w-32 bg-gray-200 rounded"></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-48 bg-gray-200 rounded"></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 rounded"></div>
          <div className="h-4 w-36 bg-gray-200 rounded"></div>
        </div>
      </div>

      {/* Footer: Links pill + Service Centers */}
      <div className="mt-auto pt-4">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-3"></div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Links pill with count badge */}
            <div className="flex items-center gap-1.5 h-6 px-2.5 bg-purple-100 rounded-full">
              <div className="w-3.5 h-3.5 bg-purple-200 rounded-full"></div>
              <div className="w-8 h-3 bg-purple-200 rounded"></div>
            </div>
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    </SkeletonCard>
  );
}

// Insurance Card Skeleton - matches InsuranceCard layout
export function InsuranceCardSkeleton() {
  return (
    <SkeletonCard>
      {/* Header: Company + Star (circle) + Type Badge (pill) + Status Badge (pill) */}
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="h-6 w-32 bg-gray-200 rounded"></div>
          <div className="w-5 h-5 bg-amber-100 rounded-full"></div>
          <div className="h-6 w-16 bg-blue-100 rounded-full"></div>
          <div className="h-6 w-14 bg-teal-100 rounded-full"></div>
        </div>
        <div className="w-8 h-8 bg-gray-200 rounded-xl"></div>
      </div>

      {/* Content: Policy number, holder name, dates */}
      <div className="space-y-3 flex-1">
        <div className="flex items-center gap-2">
          <div className="h-4 w-40 bg-gray-200 rounded"></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 rounded"></div>
          <div className="h-4 w-28 bg-gray-200 rounded"></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 rounded"></div>
          <div className="h-4 w-36 bg-gray-200 rounded"></div>
        </div>
      </div>

      {/* Footer: Links pill + Contact + ID Cards */}
      <div className="mt-auto pt-4">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-3"></div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Links pill */}
            <div className="flex items-center gap-1.5 h-6 px-2.5 bg-purple-100 rounded-full">
              <div className="w-3.5 h-3.5 bg-purple-200 rounded-full"></div>
              <div className="w-8 h-3 bg-purple-200 rounded"></div>
            </div>
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    </SkeletonCard>
  );
}

// Health Card Skeleton - matches HealthCard layout
export function HealthCardSkeleton() {
  return (
    <SkeletonCard>
      {/* Header: Name + Star (circle) + Category Badge (pill) + Open/Closed (pill) */}
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="h-6 w-36 bg-gray-200 rounded"></div>
          <div className="w-5 h-5 bg-amber-100 rounded-full"></div>
          <div className="h-6 w-20 bg-indigo-100 rounded-full"></div>
          <div className="h-5 w-14 bg-green-100 rounded-full"></div>
        </div>
        <div className="w-8 h-8 bg-gray-200 rounded-xl"></div>
      </div>

      {/* Content: Address with map pin, phone/email */}
      <div className="space-y-3 flex-1">
        <div className="flex items-start gap-1.5">
          <div className="w-4 h-4 bg-gray-200 rounded-full flex-shrink-0 mt-0.5"></div>
          <div className="h-4 w-full bg-gray-200 rounded"></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 rounded"></div>
          <div className="h-4 w-32 bg-gray-200 rounded"></div>
        </div>
      </div>

      {/* Footer: Links pill */}
      <div className="mt-auto pt-4">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-3"></div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Links pill */}
            <div className="flex items-center gap-1.5 h-6 px-2.5 bg-purple-100 rounded-full">
              <div className="w-3.5 h-3.5 bg-purple-200 rounded-full"></div>
              <div className="w-8 h-3 bg-purple-200 rounded"></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    </SkeletonCard>
  );
}

// Financial Card Skeleton - matches FinancialCard layout
export function FinancialCardSkeleton() {
  return (
    <SkeletonCard>
      {/* Header: Name + Star (circle) + Type Badge (pill) + Open/Closed (pill) */}
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="h-6 w-32 bg-gray-200 rounded"></div>
          <div className="w-5 h-5 bg-amber-100 rounded-full"></div>
          <div className="h-6 w-14 bg-indigo-100 rounded-full"></div>
          <div className="h-5 w-14 bg-green-100 rounded-full"></div>
        </div>
        <div className="w-8 h-8 bg-gray-200 rounded-xl"></div>
      </div>

      {/* Content: Account rows */}
      <div className="space-y-2 flex-1">
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-indigo-50">
          <div className="w-4 h-4 bg-indigo-200 rounded"></div>
          <div className="h-4 w-32 bg-indigo-200 rounded"></div>
          <div className="ml-auto w-2 h-2 bg-indigo-200 rounded-full"></div>
        </div>
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-indigo-50">
          <div className="w-4 h-4 bg-indigo-200 rounded"></div>
          <div className="h-4 w-28 bg-indigo-200 rounded"></div>
          <div className="ml-auto w-2 h-2 bg-indigo-200 rounded-full"></div>
        </div>
      </div>

      {/* Footer: Links pill + Location */}
      <div className="mt-auto pt-4">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-3"></div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Links pill */}
            <div className="flex items-center gap-1.5 h-6 px-2.5 bg-purple-100 rounded-full">
              <div className="w-3.5 h-3.5 bg-purple-200 rounded-full"></div>
              <div className="w-8 h-3 bg-purple-200 rounded"></div>
            </div>
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    </SkeletonCard>
  );
}

// Skeleton filter chips to match the position of real filter chips
function SkeletonFilterChips() {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4 animate-pulse">
      {/* "All" chip - selected style */}
      <div className="h-8 w-16 bg-indigo-100 rounded-full"></div>
      {/* Category chip */}
      <div className="h-8 w-20 bg-gray-100 rounded-full"></div>
    </div>
  );
}

// Section header config by variant
const sectionConfig: Record<SkeletonVariant, { icon: typeof MapPin; quickAccessTitle: string; mainTitle: string }> = {
  address: { icon: MapPin, quickAccessTitle: 'Quick Access', mainTitle: 'My Addresses' },
  personal: { icon: Wallet, quickAccessTitle: 'Quick Access', mainTitle: 'My Personal Items' },
  vehicle: { icon: Car, quickAccessTitle: 'Quick Access', mainTitle: 'My Vehicles' },
  insurance: { icon: Shield, quickAccessTitle: 'Quick Access', mainTitle: 'My Policies' },
  health: { icon: Heart, quickAccessTitle: 'Quick Access', mainTitle: 'My Providers' },
  financial: { icon: Landmark, quickAccessTitle: 'Quick Access', mainTitle: 'My Institutions' },
};

// Generic skeleton loader for card grids
// Uses cached item count and favorites count to match the user's actual data
// Includes filter chips and section headers to match the loaded state position
export function SkeletonLoader({ className = '', variant = 'address' }: { className?: string; variant?: SkeletonVariant }) {
  const SkeletonComponent = {
    address: AddressCardSkeleton,
    personal: AddressCardSkeleton,
    vehicle: VehicleCardSkeleton,
    insurance: InsuranceCardSkeleton,
    health: HealthCardSkeleton,
    financial: FinancialCardSkeleton,
  }[variant];

  // Get cached counts for this category
  const totalCount = getCachedItemCount(variant);
  const favoritesCount = getCachedFavoritesCount(variant);
  const config = sectionConfig[variant];
  const SectionIcon = config.icon;

  // If cached count is 0, show a simple loading spinner instead of skeleton cards
  // This prevents showing filter chips when there are no items
  if (totalCount === 0) {
    return (
      <div className={`${commonStyles.flexCenter} flex-col py-12 ${className}`}>
        <Loader2 className={`${commonStyles.iconSizes['3xl']} text-indigo-600 ${commonStyles.animations.spin}`} />
        <p className={`mt-4 ${commonStyles.font.sm} ${commonStyles.colors.text.gray.accessible}`}>
          Loading...
        </p>
      </div>
    );
  }

  // Use actual cached favorites count for Quick Access
  // Non-favorites go to main section
  const hasQuickAccess = favoritesCount > 0;
  const quickAccessCount = favoritesCount;
  const mainSectionCount = totalCount - favoritesCount;
  const hasMainSection = mainSectionCount > 0;

  return (
    <div className={className}>
      {/* Skeleton Filter Chips */}
      <SkeletonFilterChips />

      {/* Quick Access Section (shown if there are favorites) */}
      {hasQuickAccess && (
        <div className={hasMainSection ? "mb-16" : ""}>
          <h2 className={commonStyles.components.sectionHeader}>
            <Zap className={`${commonStyles.iconSizes.lg} text-indigo-600`} strokeWidth={2} />
            Quick Access
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 auto-rows-auto">
            {Array.from({ length: quickAccessCount }, (_, i) => (
              <SkeletonComponent key={`quick-${i}`} />
            ))}
          </div>
        </div>
      )}

      {/* Main Section (shown if there are non-favorites) */}
      {hasMainSection && (
        <div className="mt-0">
          <h2 className={commonStyles.components.sectionHeader}>
            <SectionIcon className={`${commonStyles.iconSizes.lg} text-indigo-600`} strokeWidth={2} />
            {config.mainTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 auto-rows-auto">
            {Array.from({ length: mainSectionCount }, (_, i) => (
              <SkeletonComponent key={`main-${i}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Inline loading spinner for buttons
export function InlineLoader({ size = 'sm', className = '' }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const sizeClass = size === 'sm' ? commonStyles.iconSizes.sm : size === 'md' ? commonStyles.iconSizes.md : commonStyles.iconSizes.lg;

  return (
    <Loader2 className={`${sizeClass} ${commonStyles.animations.spin} ${className}`} />
  );
}

export default LoadingState;
